import { useMemo } from "react";
import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryEntries from "@/pages/home/components/CategoryEntries";
import SectionHeading from "@/components/feature/SectionHeading";
import VideoGrid from "@/components/feature/VideoGrid";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  getFeaturedVideos,
  getLatestVideos,
  getHotVideos,
} from "@/utils/videoFilters";

export default function Home() {
  useDocumentTitle(
    "99视频 - 丰富高清视频资源观看平台",
    "99视频为您提供海量高清视频内容，涵盖电影、电视剧、综艺等多种类型，满足各类用户的观看需求，轻松搜索并发现精彩影视作品。"
  );

  const featured = useMemo(() => getFeaturedVideos(), []);
  const latest = useMemo(() => getLatestVideos(10), []);
  const hot = useMemo(() => getHotVideos(10), []);

  return (
    <div className="w-full">
      <HeroCarousel items={featured} />

      <section className="w-full max-w-page mx-auto px-4 md:px-6 py-12 md:py-16">
        <SectionHeading
          icon="ri-apps-2-line"
          title="分类快捷入口"
          subtitle="按类型快速进入频道，发现更多精彩内容"
        />
        <CategoryEntries />
      </section>

      <section className="w-full bg-background-100 border-y border-background-200">
        <div className="w-full max-w-page mx-auto px-4 md:px-6 py-12 md:py-16">
          <SectionHeading
            icon="ri-time-line"
            title="最新上线"
            subtitle="近期更新与上架的高清影视资源"
            moreLabel="查看全部电影"
            moreTo="/category/movie"
          />
          <VideoGrid videos={latest} />
        </div>
      </section>

      <section className="w-full max-w-page mx-auto px-4 md:px-6 py-12 md:py-16">
        <SectionHeading
          icon="ri-fire-line"
          title="热门推荐"
          subtitle="依据播放量排序，看看大家都在看什么"
          moreLabel="查看全部综艺"
          moreTo="/category/variety"
        />
        <VideoGrid videos={hot} />
      </section>

      <section className="w-full bg-primary-900">
        <div className="w-full max-w-page mx-auto px-4 md:px-6 py-14 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-2">
              <h2 className="text-2xl md:text-3xl font-bold text-background-50">
                关于 99视频
              </h2>
              <p className="mt-4 text-sm md:text-base text-background-200/90 leading-relaxed">
                99视频致力于为影视爱好者打造一个高清内容发现平台。我们汇集电影、电视剧、综艺等多样资源，通过清晰的分类、灵活的筛选与贴心的推荐，帮助你更快找到想看的内容，享受纯粹而舒适的浏览体验。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href="mailto:service@99video.example.com"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-accent-500 text-accent-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-send-line"></i>
                  联系我们
                </a>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-background-50/10 text-background-50 text-sm font-medium hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap"
                >
                  了解更多
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-3 lg:grid-cols-1 gap-4">
              {[
                { num: "24+", label: "精选影视" },
                { num: "3", label: "内容频道" },
                { num: "HD", label: "高清画质" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg bg-background-50/5 border border-background-50/10 px-4 py-4 text-center lg:text-left"
                >
                  <div className="text-2xl font-extrabold text-accent-400">
                    {stat.num}
                  </div>
                  <div className="text-xs text-background-200/80 mt-1">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}